// src/components/AdminUsersPage.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import AdminJourneyUsers from './AdminJourneyUsers';

// Currency list (same as your exchange rates)
const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'KRW', symbol: '₩', name: 'Korean Won' }
];

function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState({
    id: '',
    firstName: '',
    lastName: '',
    gender: '',
    email: '',
    phoneNumber: '',
    nationality: '',
    passportId: '',
    passportURL: '',
    preferredCurrency: 'USD', // Add default currency
    maxRoutes: 1, // Default route limit for creators
    isCreator: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, 'users'));
        if (!isMounted) return;
        const usersData = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          preferredCurrency: 'USD', // Default if not set
          ...docSnap.data(),
        }));
        setUsers(usersData);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Handlers for user edits
  const handleUserInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveUser = async () => {
    try {
      if (!selectedUser.id) {
        // Add new user (if needed)
        console.log('TODO: Implement addDoc for "users" collection.', selectedUser);
      } else {
        // Update existing user
        const userRef = doc(db, 'users', selectedUser.id);
        const updateData = {
          firstName: selectedUser.firstName,
          lastName: selectedUser.lastName,
          gender: selectedUser.gender,
          email: selectedUser.email,
          phoneNumber: selectedUser.phoneNumber,
          nationality: selectedUser.nationality,
          passportId: selectedUser.passportId,
          passportURL: selectedUser.passportURL,
          preferredCurrency: selectedUser.preferredCurrency,
          isCreator: selectedUser.isCreator || false,
        };

        // Only update maxRoutes if user is a creator
        if (selectedUser.isCreator) {
          updateData.maxRoutes = parseInt(selectedUser.maxRoutes) || 1;
        }

        // Remove undefined values (Firestore doesn't accept them)
        const cleanedUpdateData = Object.fromEntries(
          Object.entries(updateData).filter(([_, value]) => value !== undefined)
        );

        await updateDoc(userRef, cleanedUpdateData);
        
        // Update local state
        setUsers(users.map(u => 
          u.id === selectedUser.id ? selectedUser : u
        ));
      }
      
      // Reset form
      setSelectedUser({
        id: '',
        firstName: '',
        lastName: '',
        gender: '',
        email: '',
        phoneNumber: '',
        nationality: '',
        passportId: '',
        passportURL: '',
        preferredCurrency: 'USD',
        maxRoutes: 1,
        isCreator: false,
      });
      
      setError(''); // Clear any errors
      alert('User updated successfully!');
    } catch (err) {
      console.error('Error saving user:', err);
      setError('Failed to save user');
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser({
      ...user,
      preferredCurrency: user.preferredCurrency || 'USD',
      maxRoutes: user.maxRoutes || 1,
      isCreator: user.isCreator || false,
    });
  };

  const handleDeleteUser = async (id) => {
    // Find the user being deleted for confirmation message
    const userToDelete = users.find(u => u.id === id);
    const userName = userToDelete
      ? `${userToDelete.firstName || ''} ${userToDelete.lastName || ''}`.trim() || userToDelete.email || 'this user'
      : 'this user';

    // Confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete ${userName}?\n\n` +
      `This will permanently delete:\n` +
      `- User profile data\n` +
      `- User preferences\n\n` +
      `NOTE: This does NOT delete the Firebase Auth account. ` +
      `The user can still log in but will need to re-enter their information.\n\n` +
      `This action cannot be undone.`
    );

    if (!confirmed) {
      return; // User cancelled
    }

    try {
      // Delete user document from Firestore
      await deleteDoc(doc(db, 'users', id));

      // Remove user from local state
      setUsers(users.filter(u => u.id !== id));

      // Clear selected user if it was the deleted one
      if (selectedUser.id === id) {
        setSelectedUser({
          id: '',
          firstName: '',
          lastName: '',
          gender: '',
          email: '',
          phoneNumber: '',
          nationality: '',
          passportId: '',
          passportURL: '',
          preferredCurrency: 'USD',
          maxRoutes: 1,
          isCreator: false,
        });
      }

      setError('');
      alert(`User ${userName} deleted successfully!`);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(`Failed to delete user: ${err.message}`);
      alert(`Failed to delete user: ${err.message}`);
    }
  };

  if (loading) {
    return <div>Loading Users...</div>;
  }

  return (
    <div>
      <h2>Manage Users</h2>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      <AdminJourneyUsers
        users={users}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        handleUserInputChange={handleUserInputChange}
        handleSaveUser={handleSaveUser}
        handleEditUser={handleEditUser}
        handleDeleteUser={handleDeleteUser}
        currencies={CURRENCIES}
      />
    </div>
  );
}

export default AdminUsersPage;